import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean, useSetState } from 'react-use'
import { useQueryClient } from '@tanstack/react-query'

import { OrgBillingAPI } from '@makyoapp/makyo-sdk'
import { IAddPaymentMethodBody } from '@makyoapp/makyo-sdk/api/Organizations'
import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { PaymentCardVariant, PaymentMethodAdapter } from '@makyoapp/makyo-common'

import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useGetAddressList, useGetPaymentMethodList } from '@/resolvers/org/billing/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useReqError } from '@/hooks/useReqError'
import { AddressFormState, getInitialAddressState } from '@/components'

type Card = {
  id: string
  expires: {
    year: number
    month: number
  }
  text: string
  variant: PaymentCardVariant
  addressId: string
  nameOnCard: string
}

const usePaymentMethods = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // Get Payment Method
  const paymentMethodsQuery = useGetPaymentMethodList()
  const paymentMethods = paymentMethodsQuery.data || []

  // Get Address List
  const addressQuery = useGetAddressList()
  const addresses = addressQuery.data || []

  // Derived State
  const isLoading = paymentMethodsQuery.isLoading || addressQuery.isLoading

  // Local State
  const [showModal, setShowModal] = useState<boolean>(false)
  const defaultAddress = getInitialAddressState()
  const [editAddress, setEditAddress] = useSetState<AddressFormState>(defaultAddress)

  const [editCard, setEditCard] = useState<PaymentMethodAdapter | undefined>(undefined)
  const [cardToRemove, setCardToRemove] = useState<PaymentMethodAdapter | undefined>(undefined)

  const { currentOrg } = useCurrentOrgContext()

  const maybeDefaultId = paymentMethods?.find((d) => d.isDefault)

  const [defaultCard, setDefaultCard] = useState(maybeDefaultId ? maybeDefaultId.id : paymentMethods?.[0]?.id || '')

  const printAPIError = useReqError()

  const updateBilling = () => {
    if (currentOrg) {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orgPaymentMethodList],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orgBillingAddressList],
      })
    }
  }

  const canPm = useOrgPerm(OrgPermission.BillingManagePaymentMethods)
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  const handleAddPayment = (paymentMethod: IAddPaymentMethodBody, closeModal: () => void) => {
    if (currentOrg) {
      OrgBillingAPI.ReqAddPaymentMethod(currentOrg.id, paymentMethod)
        .then(() => {
          updateBilling()
          closeModal()
        })
        .catch(printAPIError)
    }
  }

  const makeDefault = (card: Card) => {
    if (currentOrg) {
      OrgBillingAPI.ReqSetDefaultPaymentMethod(currentOrg.id, card.id)
        .then(() => {
          setDefaultCard(card.id)
          updateBilling()
        })
        .catch(printAPIError)
    }
  }

  const handleEditCard = (card: Card, isDefault: boolean) => {
    setEditCard({
      ...card,
      isDefault,
    })
  }

  const removeCard = (card: Card) => {
    setCardToRemove(card as PaymentMethodAdapter)
  }

  const handleHide = () => setCardToRemove(undefined)

  const handleDelete = () => {
    const targetCard = cardToRemove?.id
    if (currentOrg && targetCard) {
      setIsBtnLoading(true)
      OrgBillingAPI.ReqDeletePaymentMethod(currentOrg.id, targetCard)
        .then(() => {
          toasted(t('Payment method deleted!'))
          updateBilling()
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => {
          setIsBtnLoading(false)
          setCardToRemove(undefined)
        })
    }
  }

  return {
    showModal,
    addresses: addresses || [],
    isLoading,
    paymentMethods,
    defaultCard,
    cardToRemove,
    isBtnLoading,
    editCard,
    editAddress,
    currentOrg,
    canPm,
    setShowModal,
    handleAddPayment,
    makeDefault,
    handleEditCard,
    removeCard,
    handleHide,
    handleDelete,
    setEditCard,
    setEditAddress,
    t,
  }
}

export default usePaymentMethods
